import { ref } from 'vue';
import {
  TacticalmapApi,
  ImpactTypeEnum,
  CharacterActionTypeEnum,
  type CharacterActionLog
} from '@/api/dx-backend';
import { GameApiConfig } from '@/api/backendService';
import { CharacterInfoGameService } from '@/services/characterInfoService.js';
import moveImage from '@/assets/images/action/move.png';
import diceRollImage from '@/assets/images/action/dice_roll.png';
import longRestImage from '@/assets/images/action/long_rest.webp';
import snatchImage from '@/assets/images/action/snatch.webp';
import bargainImage from '@/assets/images/action/bargain.webp';
import safeImage from '@/assets/images/action/safe.webp';
import inspectImage from '@/assets/images/action/inspect.webp';
import anomalyImage from '@/assets/images/action/anomaly.png';
import godInterventionImage from '@/assets/images/action/godintervention.png';
import physicalIcon from '@/assets/images/impact_violation/Phisical.webp';
import mentalIcon from '@/assets/images/impact_violation/Mental.webp';
import energyIcon from '@/assets/images/impact_violation/Energy.webp';
import heatIcon from '@/assets/images/impact_violation/Heat.webp';
import coldIcon from '@/assets/images/impact_violation/Cold.webp';
import lightIcon from '@/assets/images/impact_violation/Light.webp';
import darkIcon from '@/assets/images/impact_violation/Dark.webp';

export type Tone = 'damage' | 'heal' | 'buff' | 'debuff' | 'neutral';

export interface ActorInfo {
  id: string;
  name: string;
  avatar: string | null;
  relation: string;
}

export const PHASE_ACTION = 'action';
export const PHASE_PREPARATION = 'preparation';

export const TONE_BY_IMPACT: Record<string, Tone> = {
  [ImpactTypeEnum.Damage]: 'damage',
  [ImpactTypeEnum.Bleed]: 'damage',
  [ImpactTypeEnum.Burn]: 'damage',
  [ImpactTypeEnum.Poison]: 'damage',
  [ImpactTypeEnum.Lifesteal]: 'damage',
  [ImpactTypeEnum.EnergyDecrease]: 'damage',
  [ImpactTypeEnum.KnockOut]: 'damage',
  [ImpactTypeEnum.Heal]: 'heal',
  [ImpactTypeEnum.Shield]: 'heal',
  [ImpactTypeEnum.Regeneration]: 'heal',
  [ImpactTypeEnum.Absorb]: 'heal',
  [ImpactTypeEnum.Buff]: 'buff',
  [ImpactTypeEnum.Haste]: 'buff',
  [ImpactTypeEnum.Immunity]: 'buff',
  [ImpactTypeEnum.Resist]: 'buff',
  [ImpactTypeEnum.Reflect]: 'buff',
  [ImpactTypeEnum.Dodge]: 'buff',
  [ImpactTypeEnum.Debuff]: 'debuff',
  [ImpactTypeEnum.Stun]: 'debuff',
  [ImpactTypeEnum.Sleep]: 'debuff',
  [ImpactTypeEnum.Confusion]: 'debuff',
  [ImpactTypeEnum.Paralysis]: 'debuff',
  [ImpactTypeEnum.Fear]: 'debuff',
  [ImpactTypeEnum.Freeze]: 'debuff',
  [ImpactTypeEnum.Slow]: 'debuff',
  [ImpactTypeEnum.Blind]: 'debuff',
  [ImpactTypeEnum.Silence]: 'debuff',
  [ImpactTypeEnum.Disarm]: 'debuff',
  [ImpactTypeEnum.Root]: 'debuff'
};

export const GLYPH_BY_TONE: Record<string, string> = {
  damage: '⚔',
  heal: '✚',
  buff: '✦',
  debuff: '☠',
  neutral: '·'
};

export const GLYPH_BY_ACTION: Record<string, string> = {
  [CharacterActionTypeEnum.TacticalMove]: '➜',
  [CharacterActionTypeEnum.Move]: '➜',
  [CharacterActionTypeEnum.Inspect]: '◎',
  [CharacterActionTypeEnum.Gift]: '⇄',
  [CharacterActionTypeEnum.DiceRoll]: '⚄',
  BARGAIN: '⇄',
  LONG_REST: '☾',
  GOD_INTERVENTION: '✦'
};

export const MOVE_GLYPH = '➜';

export const VIOLATION_ICONS: Record<string, string> = {
  PHYSICAL: physicalIcon,
  MENTAL: mentalIcon,
  ENERGY: energyIcon,
  HEAT: heatIcon,
  FIRE: heatIcon,
  COLD: coldIcon,
  WATER: coldIcon,
  ICE: coldIcon,
  LIGHT: lightIcon,
  DARK: darkIcon,
  DARKNESS: darkIcon,
  SHADOW: darkIcon
};

export const FA_BY_ACTION: Record<string, string> = {
  [CharacterActionTypeEnum.TacticalMove]: 'fa-person-running',
  [CharacterActionTypeEnum.Move]: 'fa-person-running',
  [CharacterActionTypeEnum.ChangePosition]: 'fa-person-running',
  [CharacterActionTypeEnum.Inspect]: 'fa-eye',
  [CharacterActionTypeEnum.Gift]: 'fa-right-left',
  [CharacterActionTypeEnum.SnatchItem]: 'fa-right-left',
  [CharacterActionTypeEnum.DiceRoll]: 'fa-dice-d20',
  [CharacterActionTypeEnum.LongRest]: 'fa-moon',
  [CharacterActionTypeEnum.GodIntervention]: 'fa-bolt',
  [CharacterActionTypeEnum.Anomaly]: 'fa-bolt',
  [CharacterActionTypeEnum.StartFight]: 'fa-khanda',
  [CharacterActionTypeEnum.UseItem]: 'fa-flask',
  BARGAIN: 'fa-right-left'
};

const MOVE_ACTIONS = new Set<string>([
  CharacterActionTypeEnum.TacticalMove,
  CharacterActionTypeEnum.Move
]);

const NEGATIVE_TONES = new Set<Tone>(['damage', 'debuff']);

const COMBAT_IMPACTS = new Set<string>([
  ImpactTypeEnum.Damage,
  ImpactTypeEnum.Heal,
  ImpactTypeEnum.Shield
]);

export const actorRoster = ref<Record<string, ActorInfo>>({});
export const actorNames = ref<Record<string, string>>({});

const attemptedIds = new Set<string>();
const nameRequests = new Map<string, Promise<void>>();
let rosterRequest: Promise<void> | null = null;

const loadRoster = (): Promise<void> => {
  if (rosterRequest) return rosterRequest;
  rosterRequest = new TacticalmapApi(GameApiConfig)
    .tacticalmapPlayerCurrentRetrieve()
    .then((response: any) => {
      const roster: Record<string, ActorInfo> = {};
      const names: Record<string, string> = { ...actorNames.value };
      (response?.data?.spawned_objects || []).forEach((entry: any) => {
        if (!entry?.id) return;
        roster[entry.id] = {
          id: entry.id,
          name: entry.name || '',
          avatar: entry.avatar || null,
          relation: entry.relation || 'neutral'
        };
        if (entry.name) names[entry.id] = entry.name;
      });
      actorRoster.value = roster;
      actorNames.value = names;
    })
    .catch(() => undefined)
    .finally(() => {
      rosterRequest = null;
    });
  return rosterRequest;
};

const resolveFallbackName = (charId: string): void => {
  if (actorNames.value[charId] || nameRequests.has(charId)) return;
  const request = CharacterInfoGameService.getCharacterInfo(charId, false)
    .then((info: any) => {
      const name = info?.name;
      if (name) actorNames.value = { ...actorNames.value, [charId]: name };
    })
    .catch(() => undefined)
    .finally(() => {
      nameRequests.delete(charId);
    });
  nameRequests.set(charId, request);
};

export const collectActorIds = (actions: CharacterActionLog[]): string[] => {
  const ids: string[] = [];
  actions.forEach((action) => {
    if (action.initiator) ids.push(action.initiator);
    (action.targets || []).forEach((id: string) => id && ids.push(id));
    (action.impacts || []).forEach((impact: any) => impact?.target && ids.push(impact.target));
  });
  return ids;
};

export const ensureActors = async (ids: string[]): Promise<void> => {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const unknown = unique.filter((id) => !actorNames.value[id] && !attemptedIds.has(id));
  if (!unknown.length) return;
  unknown.forEach((id) => attemptedIds.add(id));
  await loadRoster();
  unknown.filter((id) => !actorNames.value[id]).forEach(resolveFallbackName);
};

export const actorInfo = (charId: string | null | undefined): ActorInfo | null =>
  charId ? actorRoster.value[charId] || null : null;

export const actorName = (charId: string | null | undefined): string =>
  charId ? actorNames.value[charId] || '' : '';

export const actorRelation = (charId: string | null | undefined): string =>
  actorInfo(charId)?.relation || 'neutral';

export const toneOfImpact = (impact: any): Tone => TONE_BY_IMPACT[impact?.type] || 'neutral';

export const isNegativeTone = (tone: Tone): boolean => NEGATIVE_TONES.has(tone);

export const signedText = (value: number, tone: Tone): string => {
  const magnitude = Math.abs(value);
  if (tone === 'neutral') return String(magnitude);
  return NEGATIVE_TONES.has(tone) ? `−${magnitude}` : `+${magnitude}`;
};

export const signedNumber = (value: number): string =>
  value < 0 ? `−${Math.abs(value)}` : `+${value}`;

export const dominantImpactOf = (action: CharacterActionLog): any => {
  const impacts = action.impacts || [];
  if (!impacts.length) return null;
  return impacts.reduce((best: any, current: any) =>
    Math.abs(current.size || 0) > Math.abs(best.size || 0) ? current : best
  );
};

export const violationIconOf = (violation: string | null | undefined): string | null => {
  const key = String(violation || '').toUpperCase();
  if (!key || key === 'NONE') return null;
  return VIOLATION_ICONS[key] || null;
};

export const violationIconOfAction = (action: CharacterActionLog): string | null =>
  violationIconOf(dominantImpactOf(action)?.violation);

export const skillIconOf = (action: CharacterActionLog): string | null =>
  (action.skill as any)?.icon || null;

export const ACTION_ART: Record<string, string> = {
  [CharacterActionTypeEnum.Move]: moveImage,
  [CharacterActionTypeEnum.TacticalMove]: moveImage,
  [CharacterActionTypeEnum.ChangePosition]: moveImage,
  [CharacterActionTypeEnum.DiceRoll]: diceRollImage,
  [CharacterActionTypeEnum.LongRest]: longRestImage,
  [CharacterActionTypeEnum.SnatchItem]: snatchImage,
  [CharacterActionTypeEnum.Bargain]: bargainImage,
  [CharacterActionTypeEnum.Inspect]: inspectImage,
  [CharacterActionTypeEnum.Anomaly]: anomalyImage,
  [CharacterActionTypeEnum.GodIntervention]: godInterventionImage,
  BACK_TO_SAFE_ZONE: safeImage
};

export const actionArtOf = (action: CharacterActionLog): string | null =>
  skillIconOf(action) || ACTION_ART[action.action_type as string] || null;

export const faIconOfAction = (action: CharacterActionLog): string =>
  FA_BY_ACTION[action.action_type as string] || 'fa-circle';

export const toneOfAction = (action: CharacterActionLog): Tone => {
  const dominant = dominantImpactOf(action);
  return dominant ? toneOfImpact(dominant) : 'neutral';
};

export const glyphOfAction = (action: CharacterActionLog): string => {
  const dominant = dominantImpactOf(action);
  if (dominant) {
    if (dominant.type === ImpactTypeEnum.Shield) return '⛊';
    return GLYPH_BY_TONE[toneOfAction(action)] || '·';
  }
  return GLYPH_BY_ACTION[action.action_type as string] || '·';
};

export const amountOfAction = (action: CharacterActionLog): { text: string; extra: number } | null => {
  const impacts = action.impacts || [];
  if (!impacts.length) return null;

  const tones = new Set(impacts.map(toneOfImpact));
  if (tones.size === 1) {
    const total = impacts.reduce((sum: number, impact: any) => sum + Math.abs(impact.size || 0), 0);
    if (!total) return null;
    return { text: signedText(total, toneOfAction(action)), extra: 0 };
  }

  const dominant = dominantImpactOf(action);
  const magnitude = Math.abs(dominant?.size || 0);
  if (!magnitude) return null;
  return { text: signedText(magnitude, toneOfImpact(dominant)), extra: impacts.length - 1 };
};

export const magnitudeOfAction = (action: CharacterActionLog): number =>
  (action.impacts || []).reduce(
    (max: number, impact: any) => Math.max(max, Math.abs(impact.size || 0)),
    0
  );

export const involvesCharacter = (
  action: CharacterActionLog,
  charId: string | null | undefined
): boolean => {
  if (!charId) return false;
  if (action.initiator === charId) return true;
  if ((action.targets || []).includes(charId)) return true;
  return (action.impacts || []).some((impact: any) => impact.target === charId);
};

export const isCombatAction = (action: CharacterActionLog): boolean =>
  (action.impacts || []).some((impact: any) => COMBAT_IMPACTS.has(impact.type));

export const primaryTargetOf = (action: CharacterActionLog): string | null => {
  const external = (action.targets || []).find((id: string) => id !== action.initiator);
  if (external) return external;
  const impact = (action.impacts || []).find(
    (item: any) => item.target && item.target !== action.initiator
  );
  return impact ? impact.target : null;
};

export const normalizePhase = (phase: unknown): string => {
  const value = String(phase || '').toLowerCase();
  return value === PHASE_PREPARATION ? PHASE_PREPARATION : PHASE_ACTION;
};

export const phaseOfAction = (action: CharacterActionLog): string => normalizePhase(action.phase);

export const netForCharacter = (
  actions: CharacterActionLog[],
  charId: string | null | undefined
): number => {
  if (!charId) return 0;
  let net = 0;
  actions.forEach((action) => {
    (action.impacts || []).forEach((impact: any) => {
      if (impact.target !== charId) return;
      const tone = toneOfImpact(impact);
      if (tone === 'neutral') return;
      const magnitude = Math.abs(impact.size || 0);
      net += NEGATIVE_TONES.has(tone) ? -magnitude : magnitude;
    });
  });
  return net;
};

export interface ToneAggregate {
  totals: Record<string, number>;
  moves: number;
  others: number;
}

export const aggregateActions = (actions: CharacterActionLog[]): ToneAggregate => {
  const totals: Record<string, number> = {};
  let moves = 0;
  let others = 0;

  actions.forEach((action) => {
    let counted = false;
    (action.impacts || []).forEach((impact: any) => {
      const tone = toneOfImpact(impact);
      const magnitude = Math.abs(impact.size || 0);
      if (!magnitude || tone === 'neutral') return;
      totals[tone] = (totals[tone] || 0) + magnitude;
      counted = true;
    });
    if (counted) return;
    if (MOVE_ACTIONS.has(action.action_type as string)) moves += 1;
    else others += 1;
  });

  return { totals, moves, others };
};

/* `order` restarts at zero every cycle, so it only ranks within one cycle. */
export const sortByRecency = (actions: CharacterActionLog[]): CharacterActionLog[] =>
  [...actions].sort(
    (a, b) =>
      (b.cycle?.number ?? 0) - (a.cycle?.number ?? 0) || (b.order ?? 0) - (a.order ?? 0)
  );

export const DIGEST_CYCLE_WINDOW = 2;

/**
 * The trailing cycles worth showing in the rail digest.
 *
 * Anchored on the newest cycle that actually carries actions rather than on the
 * cycle the server calls current: a cycle holds the actions queued during it and
 * only resolves at its end, so the freshly opened cycle is routinely empty while
 * every execution result sits in the one that just closed. Pinning to the current
 * cycle left the rail blank on reload with the results one cycle out of reach.
 */
export const recentCycleActions = (
  actions: CharacterActionLog[],
  window: number = DIGEST_CYCLE_WINDOW
): CharacterActionLog[] => {
  const numbers = actions
    .map((action) => action.cycle?.number)
    .filter((number): number is number => typeof number === 'number');
  if (!numbers.length) return [];
  const floor = Math.max(...numbers) - (window - 1);
  return actions.filter((action) => (action.cycle?.number ?? 0) >= floor);
};

export const sortByPriority = (
  actions: CharacterActionLog[],
  charId: string | null | undefined
): CharacterActionLog[] =>
  [...actions].sort((a, b) => {
    const selfDiff = Number(involvesCharacter(b, charId)) - Number(involvesCharacter(a, charId));
    if (selfDiff !== 0) return selfDiff;
    const magnitudeDiff = magnitudeOfAction(b) - magnitudeOfAction(a);
    if (magnitudeDiff !== 0) return magnitudeDiff;
    return (b.order ?? 0) - (a.order ?? 0);
  });
